import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "past-review-examples" }
const _hoisted_2 = { class: "examples-content" }
const _hoisted_3 = {
  key: 0,
  class: "loading"
}
const _hoisted_4 = {
  key: 1,
  class: "error"
}
const _hoisted_5 = {
  key: 2,
  class: "no-examples"
}
const _hoisted_6 = {
  key: 3,
  class: "examples-editor"
}
const _hoisted_7 = {
  key: 0,
  class: "examples-preview"
}
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = {
  key: 1,
  class: "examples-edit"
}

import { useAuthStore } from "@/stores";
import { marked } from "marked";
import { computed, onMounted, ref } from "vue";

interface TeacherExamplesResponse {
  teacherExamples: string;
  error?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PastReviewExamples',
  props: {
    assessmentId: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const authStore = useAuthStore();

const isLoading = ref(false);
const error = ref<string | null>(null);
const originalExamples = ref("");
const editableExamples = ref("");
const isEditing = ref(false);

// Helper functions for profile preferences
const getSavedExamples = (): string | undefined => {
  return authStore.currentProfile?.state?.preferences?.teacherExamples;
};

const updateExamplesPreference = (examples: string | null) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          teacherExamples: examples,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const renderedExamples = computed(() => {
  return marked(editableExamples.value || "");
});

const fetchExamples = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const assessmentId = props.assessmentId;
    if (!assessmentId) {
      throw new Error("Assessment ID is required");
    }
    const response = await fetch(
      `${apiBaseUrl}/teacher-examples/${assessmentId}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Esimerkkien lataus epäonnistui");
    }

    const data = (await response.json()) as TeacherExamplesResponse;

    if (data.error) {
      throw new Error(data.error);
    }

    originalExamples.value = data.teacherExamples;

    // Try to load saved version from profile state
    try {
      const savedExamples = getSavedExamples();
      editableExamples.value = savedExamples || originalExamples.value;
    } catch (err) {
      console.warn(
        "Failed to load teacher examples from profile state, using original:",
        err,
      );
      editableExamples.value = originalExamples.value;
    }
  } catch (err) {
    console.error("Error fetching teacher examples:", err);
    error.value = err instanceof Error ? err.message : "Tuntematon virhe";
  } finally {
    isLoading.value = false;
  }
};

const startEditing = () => {
  isEditing.value = true;
};

const saveChanges = () => {
  // Save to profile state
  updateExamplesPreference(editableExamples.value);
  isEditing.value = false;
};

const cancelEditing = () => {
  // Revert to saved version
  try {
    const savedExamples = getSavedExamples();
    editableExamples.value = savedExamples || originalExamples.value;
  } catch (err) {
    console.warn("Failed to load saved examples, using original:", err);
    editableExamples.value = originalExamples.value;
  }
  isEditing.value = false;
};

const resetToDefault = () => {
  editableExamples.value = originalExamples.value;
  updateExamplesPreference(null);
  isEditing.value = false;
};

// Expose method for external reset (when "Palauta oletustilaan" is clicked)
const resetExamples = () => {
  editableExamples.value = originalExamples.value;
  updateExamplesPreference(null);
  isEditing.value = false;
};

// Expose method to get current examples for API calls
const getCurrentExamples = () => {
  return editableExamples.value;
};

onMounted(() => {
  void fetchExamples();
});

// Expose methods for parent component
__expose({
  resetExamples,
  getCurrentExamples,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Ladataan aiempia arviointiesimerkkejä... "))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("p", null, "Virhe esimerkkien lataamisessa: " + _toDisplayString(error.value), 1),
              _createElementVNode("button", {
                onClick: fetchExamples,
                class: "retry-button"
              }, " Yritä uudelleen ")
            ]))
          : (!editableExamples.value || editableExamples.value.trim() === '')
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _cache[1] || (_cache[1] = _createElementVNode("p", null, "Ei aiempia arviointiesimerkkejä saatavilla.", -1)),
                _createElementVNode("button", {
                  onClick: startEditing,
                  class: "add-button"
                }, " Lisää esimerkkejä ")
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (!isEditing.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", { innerHTML: renderedExamples.value }, null, 8, _hoisted_8),
                      _createElementVNode("button", {
                        onClick: startEditing,
                        class: "edit-button"
                      }, "Muokkaa")
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _withDirectives(_createElementVNode("textarea", {
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editableExamples).value = $event)),
                        class: "examples-textarea",
                        placeholder: "Kirjoita aiempia arviointiesimerkkejä tähän..."
                      }, null, 512), [
                        [_vModelText, editableExamples.value]
                      ]),
                      _createElementVNode("div", { class: "edit-actions" }, [
                        _createElementVNode("button", {
                          onClick: saveChanges,
                          class: "save-button"
                        }, "Tallenna"),
                        _createElementVNode("button", {
                          onClick: cancelEditing,
                          class: "cancel-button"
                        }, " Peruuta "),
                        _createElementVNode("button", {
                          onClick: resetToDefault,
                          class: "reset-button"
                        }, " Palauta oletukseen ")
                      ])
                    ]))
              ]))
    ])
  ]))
}
}

})